import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

export default function MentorSelection({ ownerId, onMentorsSelected }) {
  const { user } = useAuth();
  const [mentors, setMentors] = useState([]);
  const [selectedMentors, setSelectedMentors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMentors = async () => {
      try {
        // Get all mentor invites for this owner
        const mentorInvitesQuery = query(
          collection(db, 'mentorInvites'),
          where('ownerId', '==', ownerId)
        );
        const mentorInvitesSnapshot = await getDocs(mentorInvitesQuery);
        
        const mentorList = [];
        mentorInvitesSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.email && data.status !== 'blocked_expired') {
            mentorList.push({
              id: doc.id,
              email: data.email,
              name: data.name || data.email.split('@')[0],
              status: data.status,
              lastAccess: data.lastUsedAt || data.acceptedAt,
              isActive: data.status === 'accepted'
            });
          }
        });

        setMentors(mentorList);
        // Pre-select active mentors
        setSelectedMentors(mentorList.filter(m => m.isActive).map(m => m.id));
      } catch (error) {
        console.error('Error fetching mentors:', error);
      } finally {
        setLoading(false);
      }
    };

    if (ownerId) {
      fetchMentors();
    }
  }, [ownerId]);

  const handleMentorToggle = (mentorId) => {
    setSelectedMentors(prev => {
      if (prev.includes(mentorId)) {
        return prev.filter(id => id !== mentorId);
      } else {
        return [...prev, mentorId];
      }
    });
  };

  const handleContinue = () => {
    const selectedMentorData = mentors.filter(m => selectedMentors.includes(m.id));
    onMentorsSelected(selectedMentorData);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading mentors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Mentors to Keep Active</h2>
        <p className="text-gray-600">
          Choose which mentors should continue to have access to your organization's Artmosphere account.
        </p>
      </div>

      <div className="space-y-3 mb-6">
        {mentors.map(mentor => (
          <div
            key={mentor.id}
            className={`border rounded-lg p-4 cursor-pointer transition-all ${
              selectedMentors.includes(mentor.id)
                ? 'border-amber-500 bg-amber-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleMentorToggle(mentor.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedMentors.includes(mentor.id)}
                  onChange={() => handleMentorToggle(mentor.id)}
                  className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                />
                <div>
                  <div className="font-medium text-gray-900">{mentor.name}</div>
                  <div className="text-sm text-gray-500">{mentor.email}</div>
                  {mentor.lastAccess && (
                    <div className="text-xs text-gray-400">
                      Last access: {new Date(mentor.lastAccess.toDate ? mentor.lastAccess.toDate() : mentor.lastAccess).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  mentor.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {mentor.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {mentors.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-4">👥</div>
          <p className="text-gray-600">No mentors found for this organization.</p>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          {selectedMentors.length} mentor{selectedMentors.length !== 1 ? 's' : ''} selected
        </div>
        <button
          onClick={handleContinue}
          disabled={selectedMentors.length === 0}
          className={`px-6 py-2 rounded-lg font-semibold ${
            selectedMentors.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-amber-600 hover:bg-amber-700 text-white'
          }`}
        >
          Continue to Plan Selection
        </button>
      </div>
    </div>
  );
}













